import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { isApiError } from '../client/errors';
import { queryKeys } from '../client/query-client';
/**
 * Invoices — the member's own receipts.
 *
 * ── Taxes are itemised and stay that way ──────────────────────────────────
 * The contract is explicit: GST and QST are each computed on the pre-tax
 * amount, and "a single blended total cannot be broken back apart". So the
 * lines are rendered as sent and never summed into one "tax" row, which is the
 * shortcut that makes a Quebec receipt wrong.
 *
 * ── `/invoices/mine`, NOT `/invoices` ─────────────────────────────────────
 * We shipped this against `GET /invoices` and it 403'd for every real member:
 * that route is `finance.read`, held by `owner` and `accountant` only. It only
 * looked like it worked because development was signed in as the bootstrap
 * owner.
 *
 * The 403 was the smaller half of the problem. `GET /invoices` is the finance
 * BOOK — unscoped, every invoice the business has ever issued, with no
 * ownership check on `/invoices/{id}` at all. The capability was doing all of
 * the scoping. Had it simply been relaxed so our page worked, every parent
 * would have been able to read every other parent's receipts.
 *
 * `/invoices/mine` is scoped by the token and takes no `userId` — the parameter
 * is omitted from the query type on purpose, because a filter a client supplies
 * is a filter a client can change.
 *
 * ── No `/invoices/mine/{id}` ──────────────────────────────────────────────
 * Deliberate, and it is why `useInvoice` is gone: a row carries its own `lines`
 * and itemised `taxes` in full, so the receipt renders from the list. A second
 * route would add an id a client could substitute and answer nothing new.
 */
export const InvoiceStatus = z.enum(['DRAFT', 'OPEN', 'PAID', 'VOID', 'UNCOLLECTIBLE']);
export const InvoiceLine = z.object({
    position: z.number().int().nonnegative(),
    description: z.string(),
    quantity: z.number().int(),
    unitPriceCents: z.number().int(),
});
export const InvoiceTaxLine = z.object({
    code: z.string(),
    /** Parts per million, because QST is 9.975% and a percent cannot hold it. */
    ratePpm: z.number().int(),
    amountCents: z.number().int(),
});
export const Invoice = z.object({
    id: z.string().uuid(),
    number: z.string(),
    status: InvoiceStatus,
    currency: z.string(),
    subtotalCents: z.number().int(),
    taxCents: z.number().int(),
    totalCents: z.number().int(),
    amountPaidCents: z.number().int(),
    issuedAt: z.string().nullable(),
    dueAt: z.string().nullable(),
    paidAt: z.string().nullable(),
    lines: z.array(InvoiceLine),
    taxes: z.array(InvoiceTaxLine),
});
const InvoiceList = z.object({
    items: z.array(Invoice),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
export function useInvoices(http, page = 1) {
    return useQuery({
        queryKey: ['invoices', page],
        queryFn: () => http.get('/invoices/mine', {
            schema: InvoiceList,
            /* `paidAt` descending is what a receipts list is for — the newest thing
             * you paid, not the oldest thing that was raised. */
            query: { page, pageSize: 20, dateField: 'paidAt' },
        }),
    });
}
/**
 * One invoice, found in the member's own list.
 *
 * ── Why a search and not a read ───────────────────────────────────────────
 * There is no `GET /invoices/mine/{id}`; see the note at the top of this file
 * for the reasoning, which was sound while the only way to reach an invoice
 * was clicking its row. The dunning mail changed that: it hands a member a URL
 * carrying one id and nothing else, and the page behind it has to find the
 * invoice. Asked for as request 19; this is what we do meanwhile.
 *
 * The list is newest-first and takes `pageSize` up to 200, so a dunning link —
 * always a recent invoice — is found in one request. The walk past page one
 * exists for a member with a long migrated history, and it is CAPPED: a page
 * view must not turn into dozens of requests because somebody has four years
 * of WordPress billing behind them. Past the cap the screen says it cannot
 * find the invoice, which is true and is not a blank page.
 */
const LOOKUP_PAGE_SIZE = 200;
const LOOKUP_MAX_PAGES = 5;
export async function findMyInvoice(http, id) {
    for (let page = 1; page <= LOOKUP_MAX_PAGES; page += 1) {
        const answer = await http.get('/invoices/mine', {
            schema: InvoiceList,
            query: { page, pageSize: LOOKUP_PAGE_SIZE, dateField: 'issuedAt' },
        });
        const found = answer.items.find((invoice) => invoice.id === id);
        if (found)
            return found;
        if (page * answer.pageSize >= answer.total)
            return null;
    }
    return null;
}
export function useMyInvoice(http, id) {
    return useQuery({
        queryKey: ['invoice', id],
        queryFn: () => findMyInvoice(http, id),
    });
}
/**
 * `POST /invoices/mine/{id}/pay` — charge the saved card for an open invoice.
 *
 * The member-initiated counterpart to the billing run: same card, same
 * settlement, same invoice, pressed rather than scheduled. The route requires
 * an `Idempotency-Key` and refuses without one; our client mints one per POST,
 * and the screen allows only one attempt in flight so a double tap cannot
 * become two charges.
 *
 * **A failure does not move the dunning ladder.** Those five attempts belong to
 * the platform, not the member — so a retry here is safe, and the screen may
 * offer one.
 */
export const InvoicePaymentOutcome = z.enum(['PAID', 'REQUIRES_ACTION', 'FAILED']);
export const InvoicePaymentResult = z.object({
    outcome: InvoicePaymentOutcome,
    /** Stripe's own word for the decline. Null on success and on a non-decline refusal. */
    declineCode: z.string().nullable(),
});
export function usePayInvoice(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => http.post(`/invoices/mine/${id}/pay`, undefined, { schema: InvoicePaymentResult }),
        onSuccess: (_result, id) => {
            void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
            void queryClient.invalidateQueries({ queryKey: ['invoices'] });
            void queryClient.invalidateQueries({ queryKey: queryKeys.entitlementAll });
        },
    });
}
/**
 * Why a payment attempt was refused, as a key.
 *
 * `REQUIRES_ACTION` is deliberately NOT here: it is not a refusal. The route's
 * own description settles it — there is no separate authentication flow, the
 * site this replaces had none, and 2 of 95 failed renewals over four years
 * were genuine authentication failures. The answer is a different card, or the
 * same one once the bank is satisfied, and the screen says so.
 */
export function invoicePaymentRefusalKey(error) {
    if (!isApiError(error))
        return 'billing.payError';
    switch (error.code) {
        case 'INVOICE_NO_PAYMENT_METHOD':
            return 'billing.payNoCard';
        case 'INVOICE_NOT_PAYABLE':
            return 'billing.payNotPayable';
        case 'INVOICE_NOT_FOUND':
            return 'billing.payNotFound';
        case 'STRIPE_NOT_CONFIGURED':
            return 'billing.payUnavailable';
        case 'IDEMPOTENCY_IN_PROGRESS':
            return 'billing.payInProgress';
        default:
            return 'billing.payError';
    }
}
//# sourceMappingURL=invoices.js.map